/**
 * 修改 Job 优先级弹窗 —— JobList 行操作菜单 与 JobTaskList 页头共用。
 *
 * 允许状态门禁:仅 job.status ∈ {未开始(3) / 排队中(5) / 编译中(7)} 时可用。
 * 门禁由调用方决定入口显隐 / 灰态,本组件只在渲染时再校验一次防误调。
 * 提交走 POST /api/job/update/priority,后端转发 CF 修改。
 */
import { useEffect, useState } from 'react';
import { Modal, Select, App, Alert } from 'antd';
import type { PriorityOption } from '@apis/simulation';
import { getPriorityList, updateJobPriority } from '@apis/simulation';
import PriorityTag from '@components/PriorityTag';

/** 允许修改优先级的 job 状态白名单:未开始 / 排队中 / 编译中。 */
export const PRIORITY_EDITABLE_STATUSES = new Set<number>([3, 5, 7]);

interface Props {
  open: boolean;
  onClose: () => void;
  /** 更新成功回调(父层 refetch 列表 / job basic info)。 */
  onSuccess?: (nextPriority: number) => void;
  job: {
    jobId: number;
    jobName?: string;
    priority: number;
    priorityName?: string;
    status: number;
  };
}

export default function UpdatePriorityModal({ open, onClose, onSuccess, job }: Props) {
  const { message } = App.useApp();
  const [options, setOptions] = useState<PriorityOption[]>([]);
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [next, setNext] = useState<number>(job.priority);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // 每次开弹窗都同步一次,防止 job.priority 变了没同步
    setNext(job.priority);
    if (options.length > 0) return;
    setLoadingOpts(true);
    getPriorityList()
      .then((list) => setOptions(list))
      .catch((err) => {
        console.error('加载优先级列表失败:', err);
        message.error('加载优先级列表失败');
      })
      .finally(() => setLoadingOpts(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job.jobId]);

  const editable = PRIORITY_EDITABLE_STATUSES.has(job.status);

  const handleOk = async () => {
    if (!editable) {
      message.error('当前 Job 状态不支持修改优先级');
      return;
    }
    if (next === job.priority) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      await updateJobPriority({ jobId: job.jobId, priority: Number(next) });
      message.success('优先级已更新');
      onSuccess?.(Number(next));
      onClose();
    } catch (err) {
      // 后端会把 CF 错误往上抛;拦截器/response 里的 code!=0 会被 axios 包成异常
      const msg = (err as { message?: string })?.message || '修改优先级失败';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="修改优先级"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={submitting}
      okButtonProps={{ disabled: !editable || next === job.priority }}
      destroyOnClose
      width={440}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!editable && (
          <Alert
            type="warning"
            showIcon
            message="当前 Job 状态不支持修改优先级(仅未开始 / 排队中 / 编译中允许)"
          />
        )}
        <div style={{ fontSize: 13, color: '#595959' }}>
          Job:{' '}
          <span style={{
            fontFamily: '"JetBrains Mono", Menlo, Consolas, monospace',
            color: '#1f1f1f',
          }}>#{job.jobId}</span>
          {job.jobName ? <span style={{ marginLeft: 8, color: '#8c8c8c' }}>{job.jobName}</span> : null}
        </div>
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: '#8c8c8c',
            marginBottom: 6,
          }}>
            <span>当前</span>
            <PriorityTag priority={job.priority} label={job.priorityName} />
          </div>
          <Select
            value={next}
            onChange={(v) => setNext(Number(v))}
            style={{ width: '100%' }}
            loading={loadingOpts}
            disabled={!editable}
            optionLabelProp="label"
            options={options.map((o) => {
              const id = Number(o.value ?? o.code);
              return {
                // label 用简单文案(Select 选中态展示);optionRender 才用 tag。
                label: o.label ?? o.name ?? String(o.value),
                value: id,
              };
            })}
            // 下拉每一项用 PriorityTag 展示;选中态回到简单文本更简洁。
            optionRender={(opt) => (
              <PriorityTag
                priority={Number(opt.value)}
                label={String(opt.label)}
              />
            )}
          />
        </div>
      </div>
    </Modal>
  );
}
